import React, { useState } from 'react';
import { EmailDesign, EmailBlock } from '../types';
import { BlockRenderer } from './BlockRenderer';
import { BlockWrapper } from './BlockWrapper';

interface EmailCanvasProps {
  design: EmailDesign;
  selectedBlockId: string | null;
  onBlockSelect: (blockId: string) => void;
  onBlockUpdate: (blockId: string, updates: Partial<EmailBlock>) => void;
  onBlockDelete: (blockId: string) => void;
  onBlockDuplicate: (blockId: string) => void;
  onDrop: (index?: number) => void;
  isDragging: boolean;
  draggedBlockType: EmailBlock['type'] | null;
  isDarkMode: boolean;
}

export const EmailCanvas: React.FC<EmailCanvasProps> = ({
  design,
  selectedBlockId,
  onBlockSelect,
  onBlockUpdate,
  onBlockDelete,
  onBlockDuplicate,
  onDrop,
  isDragging,
  draggedBlockType,
  isDarkMode
}) => {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragOver = (e: React.DragEvent, index?: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging && draggedBlockType) {
      setDragOverIndex(index ?? design.blocks.length);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index?: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    onDrop(index);
  };

  const containerStyle: React.CSSProperties = {
    backgroundColor: design.styles?.backgroundColor || '#ffffff',
    width: design.styles?.width || '600px',
    maxWidth: design.styles?.maxWidth || '600px',
    margin: '0 auto',
    minHeight: '400px',
    fontFamily: design.styles?.fontFamily || 'Arial, sans-serif',
    fontSize: design.styles?.fontSize || 14,
    color: design.styles?.textColor || '#000000'
  };

  return (
    <div className="flex justify-center p-4">
      <div
        style={containerStyle}
        className={`
          relative border-2 border-dashed rounded-lg
          ${isDragging && dragOverIndex === null 
            ? isDarkMode 
              ? 'border-accent-500 bg-accent-900/20' 
              : 'border-accent-500 bg-accent-50'
            : isDarkMode
              ? 'border-gray-600'
              : 'border-gray-300'
          }
          transition-colors
        `}
        onDragOver={(e) => handleDragOver(e)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e)}
      >
        {design.blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className={`
              mb-4 p-4 rounded-full
              ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}
            `}>
              <svg className={`w-12 h-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
            </div>
            <p className={`
              text-lg font-medium mb-2
              ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}
            `}>
              {isDragging ? 'Drop component here' : 'No content yet'}
            </p>
            <p className={`
              text-sm
              ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}
            `}>
              {isDragging 
                ? `Drop ${draggedBlockType} to add it` 
                : 'Drag components from the left sidebar to build your email'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {design.blocks.map((block, index) => (
              <React.Fragment key={block.id}>
                {/* Drop indicator */}
                {isDragging && dragOverIndex === index && (
                  <div className="h-2 bg-accent-500 rounded my-2" />
                )}
                
                <BlockWrapper
                  block={block}
                  isSelected={selectedBlockId === block.id}
                  onSelect={() => onBlockSelect(block.id)}
                  onDelete={() => onBlockDelete(block.id)}
                  onDuplicate={() => onBlockDuplicate(block.id)}
                  onUpdate={(updates) => onBlockUpdate(block.id, updates)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  isDarkMode={isDarkMode}
                >
                  <BlockRenderer
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    onSelect={() => onBlockSelect(block.id)}
                    isDarkMode={isDarkMode}
                  />
                </BlockWrapper>
              </React.Fragment>
            ))}
            
            {/* Drop indicator at end */}
            {isDragging && dragOverIndex === design.blocks.length && (
              <div className="h-2 bg-accent-500 rounded my-2" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
